import { useCallback, useMemo, useState, type ChangeEvent } from 'react'
import { Grid } from '@mui/material'
import {
  CFTemplateView,
  Select,
  TextInput,
  InfoComponent,
  ButtonV2,
} from '@labrav/react-components'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { TemplateContent } from '@labrav/react-components/lib/@types/components/CFTemplateView'
import { debounce, isEmpty, isNil } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import {
  actionTypeWrapper,
  awsMarketplaceEligibleJuridiction,
  CloudSettingsFieldDebounceTime,
  isLoading as Loading,
  isNonEmptyNumber,
  LoadingTypes,
  useAccountId,
  type CloudType,
  type ComponentLoadingState,
  type ProductType,
} from '@labrav/common'

import { FlyOutListingUserType } from '../../types/external'
import { isStringOfLength } from '../../modules/privateOffer/privateOfferCreation/utils/numberUtils'
import { useStyles } from './FlyoutAccountRegisteration.styles'
import {
  sendAccountRegistrationData,
  setTemplateInputFields,
  testTemplate,
  updateValueOnAccountDetailsForAWS,
  type AccountRegistrationRootState,
  type AWSAccountDetails,
  type TemplateRows,
} from './flyoutAccountRegistrationActions'

const validateString = (str: string, key: string) => {
  if (isEmpty(str)) {
    return `${key} is required`
  } else if (str.match('[^a-zA-Z0-9]')) {
    return `${key} cannot have special characters`
  } else {
    return ''
  }
}

export const isErrorInTemplateInputFields = (templateRows: TemplateRows[]) => {
  return templateRows.every(templateRow =>
    templateRow.inputsFields.some(inputsField => {
      return (
        isEmpty(inputsField.value) ||
        (!isEmpty(inputsField.value) && !isEmpty(inputsField.error))
      )
    })
  )
}

export const checkTestConnectionIsSuccess = (templateRows: TemplateRows[]) => {
  return templateRows.every(templateRow =>
    templateRow.inputsFields
      .filter(inputsFieldItem => inputsFieldItem.showTestStatusButton)
      .every(inputsField => inputsField.testStatus === 'success')
  )
}

export const getSellerTypeValue = (type: string) => {
  switch (type) {
    case 'ISV':
      return true
    case 'CP':
      return false
    default:
      return undefined
  }
}
export const getSellerTypeValueToDisplay = (type?: boolean) => {
  switch (type) {
    case true:
      return 'ISV'
    case false:
      return 'CP'
    default:
      return ''
  }
}

export const validateAccountIdForFlyoutAwsSetting = (
  value: string | number
) => {
  const accountIdValueForCheck = value !== '' ? (value as number) : undefined
  if (!isNonEmptyNumber(accountIdValueForCheck)) {
    return 'AWS Account ID is required'
  } else if (!isStringOfLength(12, value as string)) {
    return 'Please enter a 12 digit account ID'
  } else {
    return ''
  }
}

export type AwsAccountRegistrationFlyoutProps = {
  cloudType: CloudType
  productType: ProductType
  productAccounts?: unknown
}

type LoadingRootState = AccountRegistrationRootState & {
  loading: ComponentLoadingState
}

export const AwsAccountRegistrationFlyout = ({
  cloudType,
  productType,
}: AwsAccountRegistrationFlyoutProps) => {
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const accountId = useAccountId({ product: productType }) || ''
  const [AWSIdError, setAWSIdError] = useState<string>('')
  const hasAccounts = useSelector(
    (state: LoadingRootState) => (state.accounts.flyout || []).length > 0
  )
  const [nameError, setNameError] = useState<string>('')
  const accountRegistrationData = useSelector((state: LoadingRootState) => {
    return {
      cloudType: state.accountForm[productType].cloudType,
      rows: state.accountForm[productType].accountDetails.AWS.roles.rows,
    }
  })

  const { isIsv, externalAccountId, name, origin } = useSelector(
    (state: LoadingRootState) =>
      state.accountForm[productType].accountDetails.AWS as AWSAccountDetails
  )

  const handleInputChangeSelect = useCallback((event: unknown) => {
    const { name, value } = (event as ChangeEvent<HTMLInputElement>).target
    const actualName = name || ''
    const actualValue =
      actualName === 'isIsv' ? getSellerTypeValue(value) : value
    dispatch(
      actionTypeWrapper(
        productType,
        updateValueOnAccountDetailsForAWS(name, actualValue)
      )
    )
  }, [])

  const handleErrors = (key: string, value: string | number) => {
    if (key === 'externalAccountId') {
      const awsAccountIdErrStr = validateAccountIdForFlyoutAwsSetting(value)
      setAWSIdError(awsAccountIdErrStr)
    }
    if (key === 'name') {
      const strValidated = validateString(value as string, 'AWS Account Name')
      setNameError(strValidated)
    }
  }

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      handleErrors(name, value)
      dispatch(
        actionTypeWrapper(
          productType,
          updateValueOnAccountDetailsForAWS(name, value)
        )
      )
    },
    []
  )
  const onChangeInputFields = useCallback(
    debounce((id: string, keyName: string, value: string) => {
      dispatch(
        actionTypeWrapper(
          productType,
          setTemplateInputFields(id, keyName, value)
        )
      )
    }, CloudSettingsFieldDebounceTime),
    []
  )

  const handleArnTest = (id: string, keyName: string) => {
    dispatch(
      testTemplate(id, cloudType, productType, accountId, keyName) as never
    )
  }

  const isvFields = useMemo(
    () => ({
      title: 'Seller Type',
      placeHolder: 'Enter Seller Type',
      humanReadableKey: 'isIsv',
      id: 'isIsv',
      isReadOnly: false,
      isRequired: true,
      defaultValue: getSellerTypeValueToDisplay(isIsv),
      dataType: 'enum' as DataType,
      validationType: undefined,
      isCustom: false,
      options: [
        { label: 'ISV', value: 'ISV' },
        { label: 'CP', value: 'CP' },
      ],
    }),
    [isIsv]
  )
  const reasonForSigningUp = useMemo(
    () => ({
      title: 'Reason for signing up',
      placeHolder: 'Reason for signing up',
      humanReadableKey: 'origin',
      id: 'origin',
      isReadOnly: hasAccounts,
      isRequired: true,
      defaultValue: origin || null,
      dataType: 'enum' as DataType,
      validationType: undefined,
      isCustom: false,
      options: [
        {
          label:
            'I want to start selling on AWS Marketplace & I am a Permanent Resident/Citizen from one of the countries mentioned above.',
          value: FlyOutListingUserType.NEW,
        },
        {
          label:
            'I want to start selling on AWS Marketplace & I am NOT a Permanent Resident/Citizen from one of the countries mentioned above.',
          value: FlyOutListingUserType.MANAGED,
        },
        {
          label:
            'I am already a seller on AWS Marketplace and I want to migrate all my existing Listings to Labra.',
          value: FlyOutListingUserType.MIGRATED,
        },
      ],
    }),
    [origin, hasAccounts]
  )

  const isLoading = useSelector((state: LoadingRootState) =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )
  const isLoadingAccounts = useSelector((state: LoadingRootState) =>
    Loading(state.loading)(LoadingTypes.CMCM_FETCHING_ACCOUNTS)
  )

  const isOriginNewOrMigrated =
    origin === FlyOutListingUserType.NEW ||
    origin === FlyOutListingUserType.MIGRATED

  const isTestConnectionFails = !checkTestConnectionIsSuccess(
    accountRegistrationData.rows
  )
  const isEmptyOrErrorOnOptionalField = useMemo(
    () =>
      isEmpty(externalAccountId) ||
      isEmpty(name) ||
      !isEmpty(AWSIdError) ||
      !isEmpty(nameError) ||
      isErrorInTemplateInputFields(accountRegistrationData.rows),
    [
      externalAccountId,
      name,
      AWSIdError,
      nameError,
      accountRegistrationData.rows,
      origin,
    ]
  )
  const hasComplatedAllLoadings = isLoading || isLoadingAccounts
  const hasErrorsOnOptionalField =
    isEmptyOrErrorOnOptionalField || isTestConnectionFails
  const hasErrorOnMandatoryField = isNil(isIsv)
  const isDisabled = isOriginNewOrMigrated
    ? hasComplatedAllLoadings ||
      hasErrorsOnOptionalField ||
      hasErrorOnMandatoryField
    : hasComplatedAllLoadings || hasErrorOnMandatoryField
  const onUpdateOrSave = useCallback(() => {
    dispatch(sendAccountRegistrationData(productType, accountId) as never)
  }, [productType, accountId])

  return (
    <div className={classes.container} data-testid="aws-cloud-registration">
      <div className={classes.pageContent}>
        <div className={classes.CFTemplateContainer}>
          <div
            className={classes.infoContainer}
            data-testid="info-reason-for-signing-up"
          >
            <InfoComponent size="lg">
              <span>
                <p className={classes.infoText}>
                  Only users who are Citizens/Permanent Residents of the
                  following countries should select Option 1 in Reason for
                  signing up field: Australia, Bahrain, European Union(EU)
                  member state, Hong Kong SAR, Israel, Japan, New Zealand,
                  Norway, Qatar, Switzerland, United Arab Emirates(UAE), United
                  Kingdom(UK), United States(US). For more info click{' '}
                  <a
                    href={awsMarketplaceEligibleJuridiction}
                    className={classes.link}
                    target="_blank"
                  >
                    here
                  </a>
                  .
                </p>
              </span>
            </InfoComponent>
          </div>
          <br />
          <Select
            field={reasonForSigningUp}
            onChange={handleInputChangeSelect}
            variant="standard"
          />
          <Grid container spacing={4} className={classes.accDetails}>
            {' '}
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 3,
              }}
            >
              <Select
                variant="standard"
                field={isvFields}
                onChange={handleInputChangeSelect}
              />
            </Grid>
            {isOriginNewOrMigrated ? (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                    lg: 3,
                    xl: 3,
                  }}
                >
                  <TextInput
                    type="number"
                    keyName={'externalAccountId'}
                    isReadOnly={false}
                    title={'AWS Account ID'}
                    isRequired={true}
                    value={externalAccountId || ''}
                    placeHolder={'Enter AWS Account ID'}
                    onChange={handleInputChange}
                    isError={!isEmpty(AWSIdError)}
                    errorText={AWSIdError}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                    lg: 3,
                    xl: 3,
                  }}
                >
                  <TextInput
                    type="text"
                    keyName={'name'}
                    isReadOnly={false}
                    title={'AWS Account Name'}
                    isRequired={true}
                    value={name || ''}
                    placeHolder={'Enter AWS Account Name'}
                    onChange={handleInputChange}
                    isError={!isEmpty(nameError)}
                    errorText={!isEmpty(nameError) ? nameError : ''}
                  />
                </Grid>
              </>
            ) : (
              <></>
            )}
          </Grid>

          {isOriginNewOrMigrated ? (
            <CFTemplateView
              templateContent={
                accountRegistrationData as unknown as TemplateContent
              }
              onChangeInputFields={onChangeInputFields}
              handleTestConnection={handleArnTest}
            />
          ) : (
            <></>
          )}
        </div>
        <div className={classes.btnContainer}>
          <ButtonV2
            onClick={onUpdateOrSave}
            disabled={isDisabled}
            styleType={'primary'}
            data-testid="settings-save-update"
          >
            {!hasAccounts ? 'Save' : 'Update'}
          </ButtonV2>
        </div>
      </div>
    </div>
  )
}
